/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import EvaluationIntervalBadge from '../../components/Insights/EvaluationIntervalBadge';
import SignalInsights from '../../components/Insights/SignalInsights';
import { Lightbulb } from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import NoDataOverlay from '../../components/Insights/NoDataOverlay';
import Modal from '../../components/Modal';
import Loading from '../../components/Loading';
import PerformanceMonthPicker from '../../components/PerformanceMonthPicker';
import { SegmentedControl } from '../../components/ui';
import usePerformanceMonthFilter from '../../hooks/usePerformanceMonthFilter';
import {
  getProductivityScreenOverlayContent,
  isProductivityScreenBlocked
} from '../../constants/productivityScreenState';
import { sendRequest } from '../../utils/dateHelpers';
import {
  formatEndYearMonth,
  formatEvaluationInterval
} from '../../utils/performanceMonth';

const getScoreColor = score => {
  if (score >= 75) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
};

const getScoreBarColor = score => {
  if (score >= 75) return 'bg-green-500';
  if (score >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const TABS = [
  { key: 'overall_performance_score', label: 'Overall' },
  { key: 'performance_score', label: 'Performance' },
  { key: 'efficiency_score', label: 'Efficiency' },
  { key: 'sustainability_score', label: 'Sustainability' }
];

const PAGE_SIZES = [15, 45, 90, 120];

const TREND_TABS = [
  { key: 'overall_performance_score', label: 'Overall', color: '#6366f1' },
  { key: 'performance_score', label: 'Performance', color: '#3b82f6' },
  { key: 'efficiency_score', label: 'Efficiency', color: '#f59e0b' },
  { key: 'sustainability_score', label: 'Sustainability', color: '#10b981' }
];

const normalizeInsightsResponse = response => {
  if (!response || typeof response !== 'object') {
    return response;
  }

  const nestedResponse = response.data;
  const hasNestedPayload =
    nestedResponse &&
    typeof nestedResponse === 'object' &&
    ('selected_end_year_month' in nestedResponse ||
      'available_end_year_months' in nestedResponse ||
      'data' in nestedResponse);

  return hasNestedPayload ? nestedResponse : response;
};

const buildSparklineOptions = (seriesData, isDarkMode) => ({
  chart: {
    type: 'spline',
    backgroundColor: 'transparent',
    height: 44,
    spacing: [2, 0, 2, 0],
    animation: false
  },
  title: { text: null },
  credits: { enabled: false },
  legend: { enabled: false },
  exporting: { enabled: false },
  accessibility: { enabled: false },
  xAxis: {
    categories: seriesData.map(point => point.label),
    visible: false
  },
  yAxis: {
    min: 0,
    max: 100,
    visible: false
  },
  tooltip: {
    outside: true,
    backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
    borderColor: isDarkMode ? '#334155' : '#cbd5e1',
    style: {
      color: isDarkMode ? '#e2e8f0' : '#0f172a'
    }
  },
  plotOptions: {
    series: {
      animation: false,
      marker: {
        enabled: false
      }
    }
  },
  series: [
    {
      data: seriesData.map(point => point.overall_performance_score),
      color: '#6366f1',
      lineWidth: 2.5
    }
  ]
});

const buildAuthorTrendOptions = (seriesData, isDarkMode) => {
  const isV2Trend = seriesData.some(point => point.is_v2);

  return {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: 360,
      spacingBottom: 28
    },
    title: { text: null },
    credits: { enabled: false },
    exporting: { enabled: false },
    accessibility: { enabled: false },
    xAxis: {
      categories: seriesData.map(point => point.label),
      lineColor: isDarkMode ? '#334155' : '#cbd5e1',
      labels: {
        useHTML: true,
        style: {
          color: isDarkMode ? '#cbd5e1' : '#475569'
        },
        formatter() {
          const [startLabel = '', endLabel = ''] = String(this.value).split(
            ' - '
          );

          return `
          <span style="display:block; text-align:center; white-space:nowrap;">
            <span style="display:block;">${startLabel}</span>
            <span style="display:block; opacity:0.8;">${endLabel}</span>
          </span>
        `;
        }
      }
    },
    yAxis: {
      min: 0,
      max: 100,
      gridLineColor: isDarkMode ? '#1e293b' : '#e2e8f0',
      title: {
        text: 'Score',
        style: {
          color: isDarkMode ? '#cbd5e1' : '#475569'
        }
      },
      labels: {
        style: {
          color: isDarkMode ? '#cbd5e1' : '#475569'
        }
      }
    },
    tooltip: {
      shared: true,
      backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
      borderColor: isDarkMode ? '#334155' : '#cbd5e1',
      style: {
        color: isDarkMode ? '#e2e8f0' : '#0f172a'
      }
    },
    legend: {
      itemStyle: {
        color: isDarkMode ? '#e2e8f0' : '#0f172a'
      },
      itemHoverStyle: {
        color: isDarkMode ? '#ffffff' : '#020617'
      }
    },
    plotOptions: {
      series: {
        marker: {
          enabled: true,
          radius: 3
        }
      },
      column: {
        borderRadius: 6,
        pointPadding: 0.08,
        groupPadding: 0.12,
        pointWidth: 96
      }
    },
    series: isV2Trend
      ? [
          {
            name: 'Overall',
            data: seriesData.map(point => point.overall_performance_score),
            type: 'spline',
            color: '#6366f1',
            lineWidth: 4,
            zIndex: 3,
            marker: {
              enabled: true,
              radius: 5,
              fillColor: '#6366f1',
              lineColor: '#ffffff',
              lineWidth: 2
            }
          }
        ]
      : TREND_TABS.map(item => ({
          name: item.label,
          data: seriesData.map(point => point[item.key]),
          type: item.key === 'overall_performance_score' ? 'spline' : 'column',
          color: item.color,
          lineWidth: item.key === 'overall_performance_score' ? 4 : undefined,
          zIndex: item.key === 'overall_performance_score' ? 3 : 1,
          marker:
            item.key === 'overall_performance_score'
              ? {
                  enabled: true,
                  radius: 5,
                  fillColor: item.color,
                  lineColor: '#ffffff',
                  lineWidth: 2
                }
              : undefined,
          opacity: item.key === 'overall_performance_score' ? 1 : 0.82
        }))
  };
};

const V2DeveloperInsightsSkeleton = ({
  isDarkMode,
  ariaLabel = 'Loading developer v2 insights'
}) => (
  <div className="space-y-6 p-6" aria-label={ariaLabel} role="status">
    <div
      className={`animate-pulse rounded-2xl border p-6 shadow-sm ${
        isDarkMode
          ? 'border-strokedark bg-boxdark'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div
        className={`h-6 w-64 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}
      />
      <div
        className={`mt-3 h-4 w-96 max-w-full rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}
      />
    </div>
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {[0, 1, 2, 3].map(index => (
        <div
          key={index}
          className={`animate-pulse rounded-2xl border p-5 shadow-sm ${
            isDarkMode
              ? 'border-strokedark bg-boxdark'
              : 'border-slate-200 bg-white'
          }`}
        >
          <div
            className={`h-5 w-36 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}
          />
          <div
            className={`mt-5 h-24 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}
          />
        </div>
      ))}
    </div>
  </div>
);

const AuthorsInsights = () => {
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const [openRows, setOpenRows] = useState({});
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc');
  const [trendHistoryByMonth, setTrendHistoryByMonth] = useState({});
  const [trendLoading, setTrendLoading] = useState(false);
  const [selectedAuthorTrend, setSelectedAuthorTrend] = useState(null);
  const trendCacheRef = useRef({});
  const trendRequestIdRef = useRef(0);

  const fetchAuthorInsights = useCallback(
    endYearMonth =>
      sendRequest(
        '/performance/author-insights',
        'POST',
        endYearMonth ? { end_year_month: endYearMonth } : null
      ),
    []
  );

  const {
    selectedEndYearMonth,
    availableEndYearMonths,
    loading,
    data,
    insightFormatVersion,
    screenState,
    screenStateMeta,
    handleEndYearMonthChange
  } = usePerformanceMonthFilter(fetchAuthorInsights);

  const insights = Array.isArray(data) ? data : [];
  const overlayContent = getProductivityScreenOverlayContent(
    screenState,
    screenStateMeta
  );
  const isScreenBlocked = isProductivityScreenBlocked(screenState);

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setOpenRows({});
  }, [selectedEndYearMonth]);

  useEffect(() => {
    if (!selectedEndYearMonth || !insights.length) {
      return;
    }

    trendCacheRef.current[selectedEndYearMonth] = insights;
  }, [insights, selectedEndYearMonth]);

  useEffect(() => {
    if (availableEndYearMonths.length < 2) {
      setTrendHistoryByMonth({});
      setTrendLoading(false);
      return;
    }

    const monthsToFetch = [...availableEndYearMonths].reverse();
    const cachedHistory = monthsToFetch.reduce((acc, month) => {
      const monthData = trendCacheRef.current[month];
      if (Array.isArray(monthData)) {
        acc[month] = monthData;
      }
      return acc;
    }, {});

    setTrendHistoryByMonth(cachedHistory);

    const missingMonths = monthsToFetch.filter(
      month => !trendCacheRef.current[month]
    );

    if (!missingMonths.length) {
      return;
    }

    const requestId = trendRequestIdRef.current + 1;
    trendRequestIdRef.current = requestId;
    setTrendLoading(true);

    Promise.all(
      missingMonths.map(async month => {
        const response = normalizeInsightsResponse(
          await fetchAuthorInsights(month)
        );
        return [month, Array.isArray(response?.data) ? response.data : []];
      })
    )
      .then(entries => {
        if (trendRequestIdRef.current !== requestId) {
          return;
        }

        entries.forEach(([month, monthData]) => {
          trendCacheRef.current[month] = monthData;
        });

        setTrendHistoryByMonth(
          monthsToFetch.reduce((acc, month) => {
            if (trendCacheRef.current[month]) {
              acc[month] = trendCacheRef.current[month];
            }
            return acc;
          }, {})
        );
      })
      .catch(error => {
        if (trendRequestIdRef.current === requestId) {
          console.error('Error fetching author trend data:', error);
        }
      })
      .finally(() => {
        if (trendRequestIdRef.current === requestId) {
          setTrendLoading(false);
        }
      });
  }, [availableEndYearMonths, fetchAuthorInsights]);

  const toggleRow = uid => {
    setOpenRows(prev => ({ ...prev, [uid]: !prev[uid] }));
  };

  // Filter by search
  const filteredInsights = insights.filter(i =>
    i?.author_name?.toLowerCase().includes(search?.toLowerCase())
  );

  // Sort by active tab
  const sortedInsights = [...filteredInsights].sort((a, b) =>
    sortOrder === 'asc'
      ? (a[activeTab] || 0) - (b[activeTab] || 0)
      : (b[activeTab] || 0) - (a[activeTab] || 0)
  );
  const isV2Insights = insights.some(
    item => item?.insight_format_version === 'v2'
  );
  const v2SortedInsights = [...filteredInsights].sort((left, right) => {
    const leftScore =
      typeof left?.overall_score === 'number' ? left.overall_score : -1;
    const rightScore =
      typeof right?.overall_score === 'number' ? right.overall_score : -1;
    return rightScore - leftScore;
  });
  const v2IntervalLabel = formatEvaluationInterval(
    v2SortedInsights[0]?.start_year_month,
    v2SortedInsights[0]?.end_year_month,
    {
      separator: ' - ',
      fallback: formatEndYearMonth(selectedEndYearMonth)
    }
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedInsights.length / pageSize));
  const paginatedInsights = sortedInsights.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const authorTrendMap = useMemo(() => {
    const months = [...availableEndYearMonths].reverse();

    return months.reduce((acc, month) => {
      const monthEntries = trendHistoryByMonth[month];

      if (!Array.isArray(monthEntries)) {
        return acc;
      }

      monthEntries.forEach(entry => {
        if (!entry?.author_uid) {
          return;
        }

        if (!acc[entry.author_uid]) {
          acc[entry.author_uid] = [];
        }

        if (typeof entry.overall_score === 'number') {
          acc[entry.author_uid].push({
            month,
            label: formatEvaluationInterval(
              entry.start_year_month,
              entry.end_year_month,
              { separator: ' - ', fallback: formatEndYearMonth(month) }
            ),
            overall_performance_score: entry.overall_score,
            is_v2: true
          });
        } else if (
          TREND_TABS.every(tab => typeof entry[tab.key] === 'number')
        ) {
          acc[entry.author_uid].push({
            month,
            label: formatEvaluationInterval(
              entry.start_year_month,
              entry.end_year_month,
              { separator: ' - ', fallback: formatEndYearMonth(month) }
            ),
            overall_performance_score: entry.overall_performance_score,
            performance_score: entry.performance_score,
            efficiency_score: entry.efficiency_score,
            sustainability_score: entry.sustainability_score,
            is_v2: false
          });
        }
      });

      return acc;
    }, {});
  }, [availableEndYearMonths, trendHistoryByMonth]);

  const selectedAuthorTrendSeries = selectedAuthorTrend
    ? authorTrendMap[selectedAuthorTrend.author_uid] || []
    : [];
  const selectedAuthorTrendOptions = useMemo(
    () => buildAuthorTrendOptions(selectedAuthorTrendSeries, isDarkMode),
    [isDarkMode, selectedAuthorTrendSeries]
  );

  const isV2Insight = insightFormatVersion === 'v2' || isV2Insights;

  // Render a visible skeleton from the first request, before the API returns
  // the insight format used by this workspace.
  if (loading && !isV2Insight) {
    return (
      <V2DeveloperInsightsSkeleton
        isDarkMode={isDarkMode}
        ariaLabel="Loading developer insights"
      />
    );
  }

  if (isV2Insight) {
    if (loading || !insights.length) {
      return <V2DeveloperInsightsSkeleton isDarkMode={isDarkMode} />;
    }
    return (
      <div className="relative p-6 space-y-6">
        <div
          className={`rounded-2xl border p-6 shadow-sm ${isDarkMode ? 'border-strokedark bg-boxdark' : 'border-slate-200 bg-white'}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                Developer contribution patterns
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Evidence-based strengths and watchouts from the last 12
                completed months.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {v2IntervalLabel}
              </span>
              <PerformanceMonthPicker
                value={selectedEndYearMonth}
                options={availableEndYearMonths}
                onChange={handleEndYearMonthChange}
                disabled={loading}
              />
            </div>
          </div>
        </div>
        <input
          type="search"
          placeholder="Search by Author Name..."
          value={search}
          onChange={event => setSearch(event.target.value)}
          className={`w-full max-w-md rounded border px-3 py-2 text-sm ${isDarkMode ? 'border-strokedark bg-boxdark text-slate-100' : 'border-slate-200 bg-white text-slate-700'}`}
        />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {v2SortedInsights.map(item => {
            const limited =
              item?.overall_score_status === 'limited_observation';
            return (
              <article
                key={item.author_uid}
                className={`rounded-2xl border p-5 shadow-sm ${isDarkMode ? 'border-strokedark bg-boxdark' : 'border-slate-200 bg-white'}`}
              >
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white">
                      {item.author_name}
                    </h4>
                    <EvaluationIntervalBadge
                      startYearMonth={item.start_year_month}
                      endYearMonth={item.end_year_month}
                      compact
                    />
                  </div>
                  {!limited ? (
                    <div className="text-right">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Overall
                      </p>
                      <p className="text-3xl font-extrabold text-indigo-600 dark:text-indigo-400">
                        {item.overall_score}
                      </p>
                      {item.overall_rank ? (
                        <p className="text-xs text-slate-500">
                          Company rank #{item.overall_rank}
                        </p>
                      ) : null}
                      {item.overall_score_status === 'provisional' ? (
                        <p className="text-xs text-slate-500">Provisional</p>
                      ) : null}
                    </div>
                  ) : null}
                  {!limited &&
                  (authorTrendMap[item.author_uid] || []).length >= 2 ? (
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedAuthorTrend({
                          author_uid: item.author_uid,
                          author_name: item.author_name
                        })
                      }
                      className="h-10 w-24 shrink-0 overflow-hidden rounded-md"
                      aria-label={`Open ${item.author_name} overall-score trend`}
                    >
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={buildSparklineOptions(
                          authorTrendMap[item.author_uid],
                          isDarkMode
                        )}
                      />
                    </button>
                  ) : null}
                </div>
                {limited ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    <p className="font-semibold text-slate-900 dark:text-white">
                      Limited observation
                    </p>
                    <p className="mt-1">
                      There is not enough regular activity in this 12-month
                      period to produce a reliable score or work pattern.
                    </p>
                  </div>
                ) : (
                  <SignalInsights signals={item.signals} compact />
                )}
              </article>
            );
          })}
        </div>
        {!loading && overlayContent && (
          <NoDataOverlay
            title={overlayContent.title}
            body={overlayContent.body}
            ctaLabel={overlayContent.ctaLabel}
            ctaPath={overlayContent.ctaPath}
          />
        )}
        <Modal
          isOpen={Boolean(selectedAuthorTrend)}
          onClose={() => setSelectedAuthorTrend(null)}
          title={selectedAuthorTrend?.author_name || 'Developer Trend'}
        >
          <div className={isDarkMode ? 'text-slate-100' : 'text-slate-900'}>
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
              Overall score trend across v2 analysis periods.
            </p>
            {selectedAuthorTrendSeries.length >= 2 ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={selectedAuthorTrendOptions}
              />
            ) : null}
          </div>
        </Modal>
      </div>
    );
  }

  if (loading && !insightFormatVersion) {
    return <Loading type="card" />;
  }

  return (
    <div className="relative">
      <div
        className={`p-6 space-y-6 ${!loading && isScreenBlocked ? 'blur-sm pointer-events-none select-none' : ''}`}
      >
        <div className="flex justify-end">
          <PerformanceMonthPicker
            value={selectedEndYearMonth}
            options={availableEndYearMonths}
            onChange={handleEndYearMonthChange}
            disabled={loading}
          />
        </div>

        {/* Top Controls */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
          <div className="flex gap-2 w-2/4">
            {/* Search */}
            <input
              type="text"
              placeholder="Search by Author Name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={`px-3 py-2 rounded border text-sm w-full md:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark text-slate-100 placeholder:text-slate-500'
                  : 'border-slate-200 bg-white text-slate-700'
              }`}
            />

            {/* Page Size */}
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={`px-3 py-2 rounded border text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-full md:w-1/6 ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark text-slate-100'
                  : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              {PAGE_SIZES.map(size => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>

          <SegmentedControl
            className="w-full justify-end md:w-2/4"
            items={TABS.map(tab => ({
              value: tab.key,
              label: (
                <span className="inline-flex items-center gap-1">
                  {tab.label}
                  {activeTab === tab.key
                    ? sortOrder === 'asc'
                      ? '↑'
                      : '↓'
                    : null}
                </span>
              )
            }))}
            value={activeTab}
            onChange={tabKey => {
              if (activeTab === tabKey) {
                setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
              } else {
                setActiveTab(tabKey);
                setSortOrder('desc');
              }
            }}
          />
        </div>

        {/* Author Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {loading
            ? Array(pageSize)
                .fill(0)
                .map((_, idx) => (
                  <div
                    key={idx}
                    className={`rounded-2xl shadow p-5 flex flex-col animate-pulse min-h-[250px] ${
                      isDarkMode
                        ? 'border border-strokedark bg-boxdark'
                        : 'bg-white'
                    }`}
                  >
                    {/* Header */}
                    <div
                      className={`h-6 w-32 rounded mb-3 ${
                        isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                      }`}
                    ></div>
                    <div
                      className={`h-14 rounded-2xl mb-4 ${
                        isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                      }`}
                    ></div>

                    {/* Scores */}
                    {TABS.map(tab => (
                      <div key={tab.key} className="mb-3">
                        <div
                          className={`h-4 w-24 rounded mb-1 ${
                            isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                          }`}
                        ></div>
                        <div
                          className={`h-2 rounded-full overflow-hidden ${
                            isDarkMode ? 'bg-meta-4' : 'bg-gray-200'
                          }`}
                        >
                          <div
                            className={`h-2 rounded-full ${
                              isDarkMode ? 'bg-slate-500' : 'bg-gray-400'
                            }`}
                            style={{ width: '50%' }}
                          ></div>
                        </div>
                      </div>
                    ))}

                    {/* Reason */}
                    <div
                      className={`h-4 rounded w-3/4 mt-2 ${
                        isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                      }`}
                    ></div>

                    {/* Recommended Actions */}
                    <div
                      className={`mt-3 p-3 rounded-xl border-l-4 border-yellow-400 ${
                        isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'
                      }`}
                    >
                      {Array(3)
                        .fill(0)
                        .map((_, i) => (
                          <div
                            key={i}
                            className={`h-3 rounded w-full mb-2 ${
                              isDarkMode ? 'bg-yellow-800/70' : 'bg-yellow-200'
                            }`}
                          ></div>
                        ))}
                    </div>
                  </div>
                ))
            : paginatedInsights.map(item => {
                const adviceList = item.advices || [];
                const hasAdvice = adviceList.length > 0;

                return (
                  <div
                    key={item.author_uid}
                    className={`rounded-2xl shadow p-5 flex flex-col ${
                      isDarkMode
                        ? 'border border-strokedark bg-boxdark'
                        : 'bg-white'
                    }`}
                  >
                    {/* Header */}
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <h4 className="font-semibold text-gray-800 dark:text-gray-100">
                        {item.author_name}
                      </h4>
                      {(authorTrendMap[item.author_uid] || []).length >= 2 ? (
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedAuthorTrend({
                              author_uid: item.author_uid,
                              author_name: item.author_name
                            })
                          }
                          className="ml-auto flex h-11 w-24 shrink-0 items-center px-1"
                          aria-label={`Open ${item.author_name} trend chart`}
                        >
                          <div className="h-full w-full overflow-hidden rounded-md">
                            <HighchartsReact
                              highcharts={Highcharts}
                              options={buildSparklineOptions(
                                authorTrendMap[item.author_uid],
                                isDarkMode
                              )}
                            />
                          </div>
                        </button>
                      ) : null}
                    </div>

                    <EvaluationIntervalBadge
                      startYearMonth={item.start_year_month}
                      endYearMonth={item.end_year_month}
                      compact
                    />

                    {/* Scores */}
                    {TABS.map(tab => (
                      <div key={tab.key} className="mb-2">
                        <div className="flex justify-between text-xs text-gray-500 capitalize mb-1">
                          <span>{tab.label}</span>
                          <span className={getScoreColor(item[tab.key])}>
                            {item[tab.key]}/100
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-2 ${getScoreBarColor(item[tab.key])} transition-all`}
                            style={{ width: `${item[tab.key]}%` }}
                          />
                        </div>
                      </div>
                    ))}

                    {/* Reason */}
                    {item.overall_reason && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        {item.overall_reason}
                      </p>
                    )}

                    {/* Recommended Actions */}
                    {hasAdvice && (
                      <div className="mt-3">
                        <button
                          onClick={() => toggleRow(item.author_uid)}
                          className="flex items-center justify-between w-full text-left px-3 py-2 rounded bg-yellow-50 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300 font-semibold shadow-sm hover:shadow-md transition"
                        >
                          <div className="flex items-center gap-2">
                            <Lightbulb
                              fontSize="small"
                              className="text-yellow-500"
                            />
                            <span>Recommended Actions</span>
                          </div>
                          <span className="text-lg font-bold">
                            {openRows[item.author_uid] ? '−' : '+'}
                          </span>
                        </button>

                        {openRows[item.author_uid] && (
                          <div className="mt-2 px-3 py-2 max-h-40 overflow-y-auto bg-yellow-50 dark:bg-yellow-900 rounded-xl border-l-4 border-yellow-400 shadow-inner space-y-2">
                            {loading ? (
                              Array(3)
                                .fill(0)
                                .map((_, idx) => (
                                  <div
                                    key={idx}
                                    className="h-3 bg-yellow-200 dark:bg-yellow-800 rounded w-full animate-pulse"
                                  />
                                ))
                            ) : (
                              <ul className="list-disc list-inside space-y-1">
                                {adviceList.map((advice, idx) => (
                                  <li
                                    key={idx}
                                    className="text-sm text-gray-700 dark:text-yellow-100 flex items-start gap-2 p-1 rounded hover:bg-yellow-100 dark:hover:bg-yellow-800 transition"
                                  >
                                    <span className="text-yellow-500 mt-0.5">
                                      💡
                                    </span>
                                    <span>{advice}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6">
          {/* Authors count */}
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {Math.min(currentPage * pageSize, filteredInsights.length)}{' '}
            of {filteredInsights.length} authors
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-center items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-full disabled:opacity-50 transition ${
                isDarkMode
                  ? 'bg-meta-4 text-slate-200 hover:bg-boxdark'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              &lt;
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 rounded-full transition ${
                  currentPage === page
                    ? 'bg-blue-500 text-white'
                    : isDarkMode
                      ? 'bg-meta-4 text-slate-300 hover:bg-boxdark'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage(prev => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-full disabled:opacity-50 transition ${
                isDarkMode
                  ? 'bg-meta-4 text-slate-200 hover:bg-boxdark'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      {!loading && overlayContent && (
        <NoDataOverlay
          title={overlayContent.title}
          body={overlayContent.body}
          ctaLabel={overlayContent.ctaLabel}
          ctaPath={overlayContent.ctaPath}
        />
      )}

      <Modal
        isOpen={Boolean(selectedAuthorTrend)}
        onClose={() => setSelectedAuthorTrend(null)}
        title={selectedAuthorTrend?.author_name || 'Developer Trend'}
      >
        <div className={isDarkMode ? 'text-slate-100' : 'text-slate-900'}>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">Monthly score trend</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Overall, Performance, Efficiency, and Sustainability scores.
              </p>
            </div>
            {trendLoading ? <Loading type="inline" /> : null}
          </div>

          {selectedAuthorTrendSeries.length >= 2 ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={selectedAuthorTrendOptions}
            />
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Trend chart appears after at least 2 monthly score results are
              available for this developer.
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AuthorsInsights;
